import React, { useState, useCallback } from "react"
import { useClickOutside, useCopyFeedback } from "../../hooks"
import { downloadFile } from "../../helpers/download"
import { loginGuardProps } from "../../helpers/auth"

// Wraps a menu item's handler so the dropdown closes once it has run.
const thenClose =
  (close, fn) =>
  (...args) => {
    fn?.(...args)
    close()
  }

const SaveLoadDropdown = ({ label, title, disabled, children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const close = useCallback(() => setIsOpen(false), [])
  const dropdownRef = useClickOutside(close)

  return (
    <div className="save-load-dropdown" ref={dropdownRef}>
      <button
        className="btn-base btn-secondary save-load-dropdown-btn"
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled}
        title={title}
      >
        {label}
        <span className="save-load-dropdown-arrow">▼</span>
      </button>
      {isOpen && <div className="save-load-dropdown-menu">{children(close)}</div>}
    </div>
  )
}

const WheelActions = ({
  slices,
  result,
  displayedPrompt,
  onSaveImage,
  canSaveImage,
  onLoadWheel,
  isLoggedIn,
  onSaveToAccount,
  onLoadFromAccount,
  onShare,
}) => {
  const [shareFeedback, shareWheel] = useCopyFeedback(onShare)

  const saveWheelFile = () => {
    if (!slices) return
    const data = { slices, winner: result ?? null, prompt: displayedPrompt ?? null }
    downloadFile(JSON.stringify(data, null, 2), "spin-wheel.json", "application/json")
  }

  const loadWheelFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result)
        if (Array.isArray(data.slices)) onLoadWheel?.(data)
      } catch {
        // Invalid JSON, ignore
      }
    }
    reader.readAsText(file)
    e.target.value = ""
  }

  return (
    <>
      <div className="section-label">Wheel Share + Save</div>
      <div className="choices-input-actions">
        <button
          className="btn-base btn-secondary choices-input-btn"
          onClick={shareWheel}
          disabled={!slices}
          title="Copy shareable link to clipboard"
        >
          {shareFeedback ? "Copied!" : "Share"}
        </button>
        <button
          className="btn-base btn-secondary choices-input-btn"
          onClick={onSaveImage}
          disabled={!canSaveImage}
          title="Save wheel as image"
        >
          Capture
        </button>
      </div>
      <div className="choices-input-actions">
        <SaveLoadDropdown label="Save" title="Save wheel to file or account" disabled={!slices}>
          {(close) => (
            <>
              <button
                className="save-load-dropdown-item"
                onClick={thenClose(close, saveWheelFile)}
                title="Download wheel as JSON file"
              >
                Download
              </button>
              <button
                className="save-load-dropdown-item"
                onClick={thenClose(close, onSaveToAccount)}
                {...loginGuardProps(isLoggedIn, "Save wheel to your account")}
              >
                Account
              </button>
            </>
          )}
        </SaveLoadDropdown>
        <SaveLoadDropdown label="Load" title="Load a saved wheel">
          {(close) => (
            <>
              <label className="save-load-dropdown-item save-load-dropdown-label" title="Load wheel from JSON file">
                Upload
                <input type="file" accept=".json" onChange={thenClose(close, loadWheelFile)} />
              </label>
              <button
                className="save-load-dropdown-item"
                onClick={thenClose(close, onLoadFromAccount)}
                {...loginGuardProps(isLoggedIn, "Load wheel from your account")}
              >
                Account
              </button>
            </>
          )}
        </SaveLoadDropdown>
      </div>
    </>
  )
}

export default WheelActions
